import { memo } from 'react'

import { Add, Remove } from '@mui/icons-material'
import { Box, Button, IconButton, Paper, styled, TextField, Typography } from '@mui/material'
import { Control, Controller, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  BASE64_PATTERN,
  DrmProviderInsertFormValues,
  GUID_PATTERN,
  WELL_KNOWN_DRM_SYSTEMS,
} from 'domains/DrmProviders/staticKeyConfig'

const Panel = styled(Paper)(({ theme }) => ({
  width: '100%',
  flex: 1,
  margin: theme.spacing(-0.5),
  padding: theme.spacing(1),
  backgroundColor: theme.palette.background.levels[500],
}))

type StaticKeyConfigurationProps = {
  control: Control<DrmProviderInsertFormValues>
}

const StaticKeyConfigurationRaw = ({ control }: StaticKeyConfigurationProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const psshFieldArray = useFieldArray({ control, name: 'staticKeyConfig.encryptionDetails.pssh' })

  return (
    <>
      <Panel>
        <Typography sx={{ mb: 2 }} variant="heading-base">
          {t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.title')}
        </Typography>

        <Controller
          control={control}
          name="staticKeyConfig.encryptionDetails.keyId"
          rules={{
            required: t('domain:DrmProvider.staticKeyConfiguration.validation.required'),
            pattern: {
              value: GUID_PATTERN,
              message: t('domain:DrmProvider.staticKeyConfiguration.validation.invalidGuid'),
            },
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.keyId')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="staticKeyConfig.encryptionDetails.key"
          rules={{
            required: t('domain:DrmProvider.staticKeyConfiguration.validation.required'),
            pattern: {
              value: BASE64_PATTERN,
              message: t('domain:DrmProvider.staticKeyConfiguration.validation.invalidBase64'),
            },
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.key')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />

        <Controller
          control={control}
          name="staticKeyConfig.encryptionDetails.iv"
          rules={{
            validate: (value) =>
              !value || value.trim() === '' || BASE64_PATTERN.test(value)
                ? true
                : t('domain:DrmProvider.staticKeyConfiguration.validation.invalidBase64'),
          }}
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.iv')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message ?? t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.ivHelp')}
            />
          )}
        />

        <Controller
          control={control}
          name="staticKeyConfig.encryptionDetails.skdUri"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.encryptionDetails.skdUri')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />
      </Panel>

      <Panel>
        <Typography sx={{ mb: 1 }} variant="heading-base">
          {t('domain:DrmProvider.staticKeyConfiguration.pssh.title')}
        </Typography>
        <Typography sx={{ mb: 2, display: 'block' }} variant="text-sm" color="text.secondary">
          {t('domain:DrmProvider.staticKeyConfiguration.pssh.description')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {psshFieldArray.fields.map((item, index) => (
            <Box key={item.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Controller
                control={control}
                name={`staticKeyConfig.encryptionDetails.pssh.${index}.systemId`}
                rules={{
                  required: t('domain:DrmProvider.staticKeyConfiguration.validation.required'),
                  pattern: {
                    value: GUID_PATTERN,
                    message: t('domain:DrmProvider.staticKeyConfiguration.validation.invalidGuid'),
                  },
                }}
                render={({ field, fieldState: { invalid, error } }) => {
                  const knownSystem = WELL_KNOWN_DRM_SYSTEMS.find(
                    (system) => system.id.toLowerCase() === field.value?.toLowerCase(),
                  )

                  return (
                    <TextField
                      sx={{ flex: 1 }}
                      {...field}
                      margin="dense"
                      label={t('domain:DrmProvider.staticKeyConfiguration.pssh.systemId')}
                      type="text"
                      fullWidth
                      variant="outlined"
                      autoComplete="off"
                      error={invalid}
                      helperText={error?.message ?? knownSystem?.name}
                    />
                  )
                }}
              />
              <Controller
                control={control}
                name={`staticKeyConfig.encryptionDetails.pssh.${index}.value`}
                rules={{
                  required: t('domain:DrmProvider.staticKeyConfiguration.validation.required'),
                  pattern: {
                    value: BASE64_PATTERN,
                    message: t('domain:DrmProvider.staticKeyConfiguration.validation.invalidBase64'),
                  },
                }}
                render={({ field, fieldState: { invalid, error } }) => (
                  <TextField
                    sx={{ flex: 2 }}
                    {...field}
                    margin="dense"
                    label={t('domain:DrmProvider.staticKeyConfiguration.pssh.value')}
                    type="text"
                    fullWidth
                    variant="outlined"
                    autoComplete="off"
                    error={invalid}
                    helperText={error?.message}
                  />
                )}
              />
              <IconButton sx={{ mt: 1.5 }} size="medium" onClick={() => psshFieldArray.remove(index)}>
                <Remove fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: psshFieldArray.fields.length ? 2 : 1 }}>
          {WELL_KNOWN_DRM_SYSTEMS.map((system) => (
            <Button
              key={system.id}
              size="small"
              variant="outlined"
              startIcon={<Add />}
              onClick={() => psshFieldArray.append({ systemId: system.id, value: '' })}
            >
              {system.name}
            </Button>
          ))}
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => psshFieldArray.append({ systemId: '', value: '' })}
          >
            {t('domain:DrmProvider.staticKeyConfiguration.pssh.addCustom')}
          </Button>
        </Box>
      </Panel>

      <Panel>
        <Typography sx={{ mb: 1 }} variant="heading-base">
          {t('domain:DrmProvider.staticKeyConfiguration.license.title')}
        </Typography>
        <Typography sx={{ mb: 2, display: 'block' }} variant="text-sm" color="text.secondary">
          {t('domain:DrmProvider.staticKeyConfiguration.license.description')}
        </Typography>

        <Controller
          control={control}
          name="staticKeyConfig.license.widevineLicenseUrl"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.license.widevineLicenseUrl')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="staticKeyConfig.license.playReadyLicenseUrl"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.license.playReadyLicenseUrl')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="staticKeyConfig.license.fairPlayLicenseUrl"
          render={({ field, fieldState: { invalid, error } }) => (
            <TextField
              sx={{ flex: 1 }}
              {...field}
              margin="dense"
              label={t('domain:DrmProvider.staticKeyConfiguration.license.fairPlayLicenseUrl')}
              type="text"
              fullWidth
              variant="outlined"
              autoComplete="off"
              error={invalid}
              helperText={error?.message}
            />
          )}
        />
      </Panel>
    </>
  )
}

export const StaticKeyConfiguration = memo(StaticKeyConfigurationRaw)
